import React, { useContext, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import Layout from 'components/sales/layout/layout';
import DropDownInput from 'components/sales/Input/dropDownInput/dropDownInput';
import ConstantInput from 'components/sales/Input/constantInput/constantInput';
import { toast } from 'react-toastify';
import { BaseUrl } from 'contexts/baseUrl/baseUrl';
import { createRegularization } from 'services/darwinRecord/darwinRecordApi';
import { uploadDocumentForm } from 'services/utils/fileUpload/documentUpload';
import {
  DOCUMENT_UPLOAD_OPTIONS,
  LOCATION_OF_WORK_OPTIONS,
  REASON_FOR_MISSED_ATTENDANCE,
} from './constants';
import {
  darwinRecordListPath,
  formatDisplayDate,
  isFutureRecordDate,
  isTodayRecordDate,
  parseQueryParams,
  toDisplayDate,
} from './darwinRecordUtils';
import {
  isValidRecordDate,
  validateMarkRegularizationForm,
} from './darwinRecordValidation';
import './darwinRecord.css';

const MarkRegularization = ({ history, location }) => {
  const { baseUrl } = useContext(BaseUrl);
  const query = parseQueryParams(location?.search);
  const queryDate = query.date || '';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [attachmentName, setAttachmentName] = useState('');
  const [formData, setFormData] = useState({
    reason_for_missed_attendance: '',
    location_of_work: '',
    date: toDisplayDate(queryDate) || queryDate,
    check_in_time: '',
    check_out_time: '',
    remarks: '',
  });

  useEffect(() => {
    if (!queryDate || !isValidRecordDate(queryDate)) {
      toast.error('Invalid or missing date for regularization');
      history.replace(darwinRecordListPath(baseUrl));
      return;
    }
    if (isFutureRecordDate(queryDate)) {
      toast.error('You cannot mark regularization for a future date');
      history.replace(darwinRecordListPath(baseUrl));
      return;
    }
    if (isTodayRecordDate(queryDate)) {
      toast.error('You cannot mark regularization for today');
      history.replace(darwinRecordListPath(baseUrl));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      date: toDisplayDate(queryDate) || queryDate,
    }));
  }, [queryDate, baseUrl, history]);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const url = await uploadDocumentForm(file, DOCUMENT_UPLOAD_OPTIONS);
      setAttachmentUrl(url);
      setAttachmentName(file.name);
      toast.success('Attachment uploaded');
    } catch (error) {
      toast.error(error?.message || 'Failed to upload attachment');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validation = validateMarkRegularizationForm(formData);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    setIsSubmitting(true);
    const { error } = await createRegularization({
      ...validation.payload,
      attachment: attachmentUrl || '',
      regularization_mode: 0,
    });
    setIsSubmitting(false);

    if (error) {
      toast.error(error);
      return;
    }

    toast.success('Regularization request raised');
    setTimeout(() => {
      history.push(darwinRecordListPath(baseUrl));
    }, 700);
  };

  const displayDate = formatDisplayDate(formData.date);

  return (
    <Layout
      title="Mark regularization"
      isGoback
      linkFlag
      linkUrl={darwinRecordListPath(baseUrl)}
    >
      <form onSubmit={handleSubmit} className="darwin-record-page darwin-regularization-form">
        <div className="darwin-details-panel">
          <div className="darwin-details-panel__header">
            <div className="darwin-details-panel__identity">
              <h2 className="darwin-details-panel__name">Regularization request</h2>
              <p className="darwin-details-panel__email">
                Submit attendance correction for{' '}
                <strong className="darwin-regularization-form__date">{displayDate}</strong>
              </p>
            </div>
          </div>

          <div className="darwin-details-panel__body">
            <div className="darwin-form-row-grid">
              <DropDownInput
                label="Reason for missed attendance"
                data={REASON_FOR_MISSED_ATTENDANCE}
                required
                isStarRequired
                getIdValue={(value) =>
                  updateField('reason_for_missed_attendance', value?.id || '')
                }
              />
              <DropDownInput
                label="Location of work"
                data={LOCATION_OF_WORK_OPTIONS}
                required
                isStarRequired
                getIdValue={(value) => updateField('location_of_work', value?.id || '')}
              />
            </div>

            <div className="darwin-details-meta-grid darwin-regularization-form__timing">
              <ConstantInput
                label="Date"
                value={formData.date}
                disabled
                required
                isStarRequired
              />
              <ConstantInput
                label="From"
                type="time"
                value={formData.check_in_time}
                handleChange={(val) => updateField('check_in_time', val)}
                required
                isStarRequired
              />
              <ConstantInput
                label="To"
                type="time"
                value={formData.check_out_time}
                handleChange={(val) => updateField('check_out_time', val)}
                required
                isStarRequired
              />
            </div>

            <div className="darwin-details-section">
              <label className="darwin-details-section__label" htmlFor="regularization-reason">
                Reason <span className="darwin-required-mark">*</span>
              </label>
              <textarea
                id="regularization-reason"
                className="darwin-details-textarea"
                rows={4}
                value={formData.remarks}
                onChange={(event) => updateField('remarks', event.target.value)}
                placeholder="Describe why regularization is needed"
              />
            </div>

            <div className="darwin-details-section">
              <span className="darwin-details-section__label">File attachment</span>
              <div className="darwin-file-upload">
                <label className="darwin-file-upload__trigger" htmlFor="regularization-attachment">
                  Choose file
                </label>
                <input
                  id="regularization-attachment"
                  type="file"
                  className="darwin-file-upload__input"
                  onChange={handleFileChange}
                />
                {attachmentName ? (
                  <span className="darwin-file-upload__name">{attachmentName}</span>
                ) : (
                  <span className="darwin-file-upload__hint">Optional supporting document</span>
                )}
              </div>
            </div>
          </div>

          <div className="darwin-details-panel__footer">
            <Button
              type="submit"
              variant="primary"
              className="darwin-details-action-btn darwin-details-action-btn--approve"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit request'}
            </Button>
          </div>
        </div>
      </form>
    </Layout>
  );
};

export default MarkRegularization;
